import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Box,
  IconButton,
  TableContainer,
  Card,
  CardContent,
  CircularProgress, // <-- Add this import
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Search, Print } from "@mui/icons-material";
import logo from "./assets2/logo.png";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const Accountars = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // <-- Add loading state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const dateInputRef = useRef(null);
  const theme = useTheme();
  const isMobileOrTab = useMediaQuery(theme.breakpoints.down("md")); // true for mobile & tablet

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // Start loading
        const response = await fetch("https://enzo-matr.onrender.com/api/products");
        const result = await response.json();

        // Format the date for all rows
        const formattedData = result.map((item) => ({
          ...item,
          date: new Date(item.date).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          }),
        }));

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchData();
  }, []);

  const totalInvoiceCount = data.length;
  const totalIncome = data.reduce((sum, row) => sum + (row.price || 0), 0);

  const handleDateButtonClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const filteredData = data.filter((row) => {
    const matchesSearch = searchTerm === "" || row.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth =
      selectedMonth === "All" ||
      new Date(row.date).toLocaleString("default", { month: "long" }) === selectedMonth;
    const matchesDate = !selectedDate || row.date === new Date(selectedDate).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });

    return viewAll || (matchesSearch && matchesMonth && matchesDate);
  });

  const filteredInvoiceCount = filteredData.length;
  const uniqueDatesCount = new Set(filteredData.map((row) => row.date)).size;

  const filteredDates = filteredData.map((row) => new Date(row.date));
  const startingDate = filteredDates.length > 0 ? new Date(Math.min(...filteredDates)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  }) : "N/A";
  const endingDate = filteredDates.length > 0 ? new Date(Math.max(...filteredDates)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  }) : "N/A";

  const handleDateChange = (event) => {
    const date = event.target.value;
    setSelectedDate(date);
    setViewAll(false); // Disable "View All" when a date is selected
  };

  const handleSearch = () => {
    setViewAll(false); // Disable "View All" when searching
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setViewAll(false); // Disable "View All" when a month is selected
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Table</title>
          <style>
            table {
              width: 80%;
              border-collapse: collapse;
            }
            th, td {
              padding: 4px;
              text-align: center;
            }
            th {
              background-color: #eee !important;
              font-weight: bold;
              border: black;
            }
            td {
              border-top: black;
              border-bottom: black;
            }
            .print-header {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 16px;
            }
            .print-header img {
              width: 24px;
              height: 24px;
              margin-right: 8px;
            }
            .print-header span {
              font-size: 18px;
              font-weight: bold;
            }
            .print-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 16px;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
            }
            .print-table th, .print-table td {
              padding: 8px;
              text-align: center;
            }
            .print-table th {
              background-color: #eee !important;
              font-weight: bold;
            }
            .print-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .print-table tr:nth-child(odd) {
              background-color: #fff;
            }
            @media print {
              .print-table th {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                background-color: #eee !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <img src="${logo}" alt="logo" />
            <span>Company Name (Expense)</span>
          </div>
          <div class="print-info">
            <span>Total Products: ${totalInvoiceCount}</span>
            <span>Total Income: ${totalIncome}</span>
          </div>
          <table class="print-table">
            <thead>
              <tr>
                ${["Date", "Name", "Discount", "VAT", "Amount"].map((head) => `
                  <th>${head}</th>
                `).join("")}
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((row, i) => `
                <tr>
                  <td>${row.date}</td>
                  <td>${row.name}</td>
                  <td>${row.discount}</td>
                  <td>${row.vat}</td>
                  <td>${row.price}</td>
                </tr>
              `).join("")}
              <tr>
                <td colSpan="5" style="border-bottom: 2px solid black;"></td>
              </tr>
              <tr>
                <td style="text-align: right;">Tot Date: ${uniqueDatesCount}</td>
                <td>Total Products: ${filteredInvoiceCount}</td>
                <td>Total Discount: ${filteredTotalDiscount}</td>
                <td>Total VAT: ${filteredTotalVAT}</td>
                <td>Total Expence: ${filteredTotalIncome}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate totals based on filteredData, not data
  const filteredTotalDiscount = filteredData.reduce((sum, row) => sum + (row.discount || 0), 0);
  const filteredTotalVAT = filteredData.reduce((sum, row) => sum + (row.vat || 0), 0);
  const filteredTotalIncome = filteredData.reduce((sum, row) => sum + (row.price || 0), 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          backgroundColor: "#f4f4f4",
          display: "flex",
          ml: isMobileOrTab ? "17px" : 0, // <-- Add margin-left for mobile/tablet
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, mt: '7px' }}>
          <Paper sx={{ p: 1, width: isMobileOrTab? "690px":"600px", maxHeight: 390, borderRadius: "8px", boxShadow: 3, display: "flex", fontFamily: "Canada Serif, serif", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ backgroundColor: "#333", color: "#fff", p: 1, fontFamily: "Canada Serif, serif", fontWeight: "bold", textAlign: "center", borderRadius: "4px" }}>
              PRODUCT EXPENSE
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search Product Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flexGrow: 1, backgroundColor: "#fff", borderRadius: "10px", width: "auto" }}
              />
              <IconButton
                sx={{ backgroundColor: "#000", color: "#fff", fontFamily: "Canada Serif, serif", borderRadius: "6px", width: 36, height: 36 }}
                onClick={handleSearch} // Trigger search on click
              >
                <Search />
              </IconButton>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#000", color: "#fff", fontFamily: "Canada Serif, serif", fontWeight: "bold", borderRadius: "10px", border: "2px solid black", height: "36px" }}
                onClick={() => setViewAll(!viewAll)}
              >
                ViewAll
              </Button>
              <Select
                size="small"
                value={selectedMonth}
                onChange={handleMonthChange} // Trigger month change
                sx={{ backgroundColor: "#FFB400", color: "#000", fontFamily: "Canada Serif, serif", fontWeight: "bold", borderRadius: "10px", border: "2px solid black", height: "36px" }}
              >
                <MenuItem value="All">Month</MenuItem>
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"
                ].map((month) => (
                  <MenuItem key={month} value={month}>{month}</MenuItem>
                ))}
              </Select>
              <IconButton
                sx={{ backgroundColor: "#fdb913", borderRadius: "9px", height: "35px", border: "2px solid black" }}
                onClick={handleDateButtonClick}
              >
                <CalendarMonthIcon />
                <input
                  type="date"
                  ref={dateInputRef}
                  style={{ width: "0px", height: "0px", backgroundColor: "#FFB400", border: "none" }}
                  onChange={handleDateChange} // Trigger date change
                />
              </IconButton>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ textAlign: "center", fontFamily: "Canada Serif, serif", fontWeight: "bold", fontSize: "18px" }}>
                <img src={logo} alt="fire" style={{ width: 44, height: 24, paddingBottom: 10, verticalAlign: "middle" }} />
                <span style={{ fontSize: "20px", marginLeft: -1 }}>Company Name (EXPENSE)</span>
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "14px", textAlign: "left", fontFamily: "Canada Serif, serif", fontWeight: "bold" }}>
                  Total Product Count: {totalInvoiceCount}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px", fontFamily: "Canada Serif, serif", textAlign: "right", fontWeight: "bold" }}>
                  {startingDate} to {endingDate}
                </Typography>
              </Box>
            </Box>
            <TableContainer
              sx={{
                height: "194px",
                overflowY: "auto",
                mt: 2,
                "&::-webkit-scrollbar": { display: "none" },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#eee" }}>
                    {["Date", "Name", "Tot Discount", "VAT", "Amount"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: "bold", fontFamily: "Canada Serif, serif", textAlign: "center", padding: "6px 8px", fontSize: "12px", borderBottom: "2px solid black", backgroundColor: "#eee" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress
                          size={40}
                          thickness={5}
                          sx={{
                            color: "#FFB400", // Custom color
                            '& .MuiCircularProgress-circle': { stroke: "#000" }, // Optional: inner color
                          }}
                        />
                        <Typography sx={{ mt: 1, color: "#FFB400", fontWeight: "bold" }}>
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length > 0 ? (
                    filteredData.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.date}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.name}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.discount}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.vat}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.price}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", fontFamily: "Canada Serif, serif", padding: "10px" }}>
                        No matching records found
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ position: "sticky", bottom: "28px", backgroundColor: "#eee", zIndex: 2 }}>
                    <TableCell colSpan={5} sx={{ borderBottom: "2px solid black", padding: "0" }} />
                  </TableRow>
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#eee",
                      zIndex: 2,
                      borderTop: "2px solid black",
                      boxShadow: "0px -2px 5px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <TableCell sx={{ textAlign: "right", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      Tot Date: {uniqueDatesCount}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      Total Products: {filteredInvoiceCount}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredTotalDiscount}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredTotalVAT}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredTotalIncome}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Card sx={{ width: isMobileOrTab? 705:620, backgroundColor: "#000", color: "#fff", textAlign: "center", fontFamily: "Canada Serif, serif", borderRadius: "8px", boxShadow: 3, height: "99px", mt: -1 }}>
            <CardContent sx={{ padding: "10px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "13px", textAlign: "left", fontFamily: "Canada Serif, serif" }}>
                  TOTAL EXPENSE: {filteredTotalIncome}/-
                </Typography>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: "8px" }}>Tot Date: {uniqueDatesCount}</Typography>
                  <Typography sx={{ fontSize: "8px" }}>Total Products: {filteredInvoiceCount}</Typography>
                  <Typography sx={{ fontSize: "8px" }}>Total VAT: {filteredTotalVAT}</Typography>
                </Box>
              </Box>
              <Button variant="contained" fullWidth sx={{ mt: 1, backgroundColor: "#FFB400", padding: "6px 225px", color: "#000", fontWeight: "bold", fontFamily: "Canada Serif, serif", borderRadius: "6px", fontSize: "15px" }} startIcon={<Print sx={{ fontSize: "60px" }} />} onClick={handlePrint}>
                PRINT EXPENSE
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Accountars;